import type { QueryClient } from '@tanstack/vue-query'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import { APIError } from '~~/src/api/_error/APIError'
import { favoriteElemental, removeElementalFavorite } from '~~/src/api/elementals'
import type { Elemental } from '~~/src/types/elemental'

const updateElementalInQueryCache = (queryClient: QueryClient, elemental: Elemental, id: string) => {
  queryClient.setQueryData([TanStackQueryIds.ELEMENTAL, elemental.elementalTypeId, id], (old: any) => {
    if (!old)
      return old
    return { ...old, isFavorite: !old.isFavorite }
  })
}

export const useFavoriteMutation = () => {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation<Awaited<ReturnType<typeof favoriteElemental>>, Error, { elemental: Elemental }>({
    mutationKey: ['favorite-item'],
    mutationFn: async ({ elemental }) => {
      if (elemental.isFavorite)
        return await removeElementalFavorite(elemental.id)
      return await favoriteElemental(elemental.id)
    },
    onMutate: ({ elemental }) => {
      updateElementalInQueryCache(queryClient, elemental, elemental.id)
      updateElementalInQueryCache(queryClient, elemental, elemental.url)
    },
    onError: (error) => {
      if (error instanceof APIError) {
        toast.error(error.message, {})
        return
      }

      toast.error('Unable to favorite this item')
    },
    onSuccess: (_, { elemental }) => {
      queryClient.refetchQueries({
        queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elemental.elementalTypeId],
        exact: false,
        type: 'all',
      })

      queryClient.invalidateQueries({
        queryKey: [TanStackQueryIds.RECENT_ELEMENTAL],
        type: 'all',
      })

      queryClient.refetchQueries({ queryKey: [TanStackQueryIds.FAVORITES] })
      toast.success(`Item ${elemental.isFavorite ? 'unfavorited' : 'favorited'}`)
    },
  })
}
